import { Check, ChevronRight, CircleAlert, CircleHelp, Copy, FilePenLine, ShieldCheck, X } from "lucide-react";
import { useState } from "react";
import type { AttentionKind, SessionManagementMode, SessionState } from "../domain";
import { useI18n } from "../i18n";

export function Status({ state }: { state: SessionState }) {
  const { t } = useI18n();
  const labels: Record<SessionState, string> = {
    starting: t("status.starting"),
    running: t("status.running"),
    waiting: t("status.waiting"),
    stopping: t("status.stopping"),
    stopped: t("status.stopped"),
    done: t("status.completed"),
    error: t("status.error"),
    exited: t("status.exited"),
    idle: t("status.idle"),
  };
  return <span className={`status status-${state}`}><i />{labels[state]}</span>;
}

export function ProfileBadge({ mode }: { mode: SessionManagementMode }) {
  const { t } = useI18n();
  return <span className={`profile-badge profile-${mode}`}>{mode === "orni_work" ? "Orni Work" : t("profile.native")}</span>;
}

export function AttentionIcon({ kind }: { kind: AttentionKind }) {
  const Icon = kind === "question" ? CircleHelp : kind === "review" ? ShieldCheck : kind === "change" ? FilePenLine : kind === "blocked" ? CircleAlert : Check;
  return <span className={`attention-icon attention-${kind}`}><Icon size={15} /></span>;
}

export function SectionTitle({ eyebrow, title, action, onAction }: { eyebrow?: string; title: string; action?: string; onAction?: () => void }) {
  return <div className="section-title"><div>{eyebrow ? <span>{eyebrow}</span> : null}<h2>{title}</h2></div>{action ? <button type="button" onClick={onAction}>{action}<ChevronRight size={13} /></button> : null}</div>;
}

export function Progress({ value }: { value: number }) {
  return <span className="progress"><i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} /></span>;
}

export function ConfirmDialog({ title, message, confirmLabel, onConfirm, onCancel }: { title: string; message: string; confirmLabel: string; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useI18n();
  return <div className="dialog-backdrop confirm-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}><section className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title"><header><div><strong id="confirm-dialog-title">{title}</strong><p>{message}</p></div><button type="button" onClick={onCancel} aria-label={t("common.close")}><X size={16} /></button></header><footer><button className="ghost-button" type="button" onClick={onCancel}>{t("common.cancel")}</button><button className="primary-button confirm-danger" type="button" onClick={onConfirm}>{confirmLabel}</button></footer></section></div>;
}

export function CommandDialog({ title, description, command, onClose }: { title: string; description: string; command: string; onClose: () => void }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  return <div className="dialog-backdrop confirm-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><section className="confirm-dialog command-dialog" role="dialog" aria-modal="true" aria-labelledby="command-dialog-title"><header><div><strong id="command-dialog-title">{title}</strong><p>{description}</p></div><button type="button" onClick={onClose} aria-label={t("common.close")}><X size={16} /></button></header><div className="command-dialog-body"><code>{command}</code><button className="ghost-button" type="button" onClick={() => void copyCommand()}><Copy size={13} />{copied ? t("common.copied") : t("common.copyCommand")}</button><p>{t("dialog.commandHint")}</p></div><footer><button className="primary-button" type="button" onClick={onClose}>{t("common.gotIt")}</button></footer></section></div>;
}
